import { all, any, BROKER, ctl, ctx, DECLARATION, field, has, industry, is, na, not, test, unmapped, val, withBlocks, yn, } from './helpers.js'; // prettier-ignore
/**
 * QBE Insurance (Malaysia) Berhad, Cyber and Data Security (Auto-bind), form CDSAB006-PF-0924. For
 * businesses with revenue up to RM30m earned in Malaysia, outside the excluded industries. Any "No" to the
 * statements in B and C refers the case to QBE's underwriters instead of binding automatically.
 */
/** Any "No" to these statements in B and C refers the case to QBE's underwriters (task 91). */
const REFERRED = new Set([
    'revenue_within_limit', 'revenue_in_malaysia', 'malware_protection', 'not_healthcare', 'not_social_ecommerce',
    'not_hosting', 'not_energy', 'not_public', 'not_financial', 'not_digital_assets', 'pdpa_registered',
    'mfa_remote_admin', 'no_outage', 'no_incident',
]); // prettier-ignore
const REFER = {
    in: ['no'],
    effect: 'refer',
    note: 'A “No” here refers the case to QBE’s underwriters instead of binding it automatically.',
};
/** Yes and No on QBE's radio groups: the first box is "Yes". */
const YES_NO = { yes: '/0', no: '/1' };
const b = (group) => ({ radio: `b1 Group${group}`, states: YES_NO });
/**
 * The widgets of QBE's fillable PDF (task 92), mapped against the blank form. The PDF has no box for the
 * PDPA certificate date. Declarations, signatures and the broker's fields are left to them.
 */
const PDF = {
    sha256: 'ed6617758bfe738895bce67f18ffbb6206f4c18ab751f45ee8ef6218690b8a9d',
    pages: 4,
    fields: {
        proposer_name: 'a 5',
        correspondence_address: { lines: ['a 6', 'a 7'] },
        city: 'a 9',
        state: 'a 10',
        country: 'a 11',
        contact_number: 'a 12',
        email: 'a 13',
        occupation: 'a 14',
        tax_no: 'a b 2a',
        registration_no: 'a b 2b',
        sst_no: 'a b 2c',
        office_tel: 'a b 2d',
        nature_of_business: 'a b 3a',
        established: 'a b 3b',
        legal_name: 'b1 Legal entity name of applicant',
        applicant_registration_no: 'b1 Registration No',
        industry: 'b1 Business occupationindustry',
        website: 'b1 www',
        principal_address: 'b1 undefined_2',
        applicant_email: 'b1 undefined_3',
        applicant_contact: 'b1 Contact No_2',
        revenue_within_limit: b(2),
        revenue_in_malaysia: b(3),
        malware_protection: b(4),
        not_healthcare: b(5),
        not_social_ecommerce: b(6),
        not_hosting: b(7),
        not_energy: b(8),
        not_public: b(9),
        not_financial: b(10),
        not_digital_assets: b(11),
        pdpa_registered: b(12),
        mfa_remote_admin: b(13),
        no_outage: { radio: 'c Group10', states: YES_NO },
        no_incident: { radio: 'c Group11', states: YES_NO },
        applicant_name: 'd e Text3',
    },
    // The premium table is one radio group: a box for each limit in each revenue band, with and without the
    // business interruption and extortion extension.
    quote: {
        radio: 'b2 Group14',
        states: {
            'main:300000:0': '/0',
            'main:300000:1': '/1',
            'main:300000:2': '/2',
            'main:300000:3': '/3',
            'main:300000:4': '/4',
            'main:750000:0': '/5',
            'main:750000:1': '/6',
            'main:750000:2': '/7',
            'main:750000:3': '/8',
            'main:750000:4': '/9',
            'main:1500000:0': '/10',
            'main:1500000:1': '/12',
            'main:1500000:2': '/13',
            'main:1500000:3': '/14',
            'main:1500000:4': '/15',
            'main:3000000:0': '/11',
            'main:3000000:1': '/16',
            'main:3000000:2': '/17',
            'main:3000000:3': '/18',
            'main:3000000:4': '/19',
            'bi_extortion:300000:0': '/20',
            'bi_extortion:300000:1': '/24',
            'bi_extortion:300000:2': '/25',
            'bi_extortion:300000:3': '/26',
            'bi_extortion:300000:4': '/27',
            'bi_extortion:750000:0': '/21',
            'bi_extortion:750000:1': '/28',
            'bi_extortion:750000:2': '/29',
            'bi_extortion:750000:3': '/30',
            'bi_extortion:750000:4': '/31',
            'bi_extortion:1500000:0': '/22',
            'bi_extortion:1500000:1': '/32',
            'bi_extortion:1500000:2': '/33',
            'bi_extortion:1500000:3': '/34',
            'bi_extortion:1500000:4': '/35',
            'bi_extortion:3000000:0': '/23',
            'bi_extortion:3000000:1': '/36',
            'bi_extortion:3000000:2': '/37',
            'bi_extortion:3000000:3': '/38',
            'bi_extortion:3000000:4': '/39',
        },
    },
};
const FORM = {
    insurer: 'QBE Insurance (Malaysia) Berhad',
    product: 'Cyber and Data Security (Auto-bind)',
    form_code: 'CDSAB006-PF-0924',
    description: 'Auto-bind cyber cover for businesses with revenue up to RM30m. Any “No” in sections B or C is referred to QBE.', // prettier-ignore
    source_document: 'QBE - CDSAB006-PF-0924-Cyber&Data Security (Auto-bind).pdf',
    // The premiums printed on the form (main cover, and with the business interruption and extortion
    // extension), before taxes and stamp duty, by revenue for the current financial year.
    rating: {
        bands: [6_000_000, 10_000_000, 15_000_000, 20_000_000, 30_000_000],
        extensions: { bi_extortion: 'Cyber business interruption and cyber extortion extension' },
        note: 'QBE’s printed auto-bind premiums, excluding applicable taxes and stamp duty. Deductible RM15,000 each and every claim.', // prettier-ignore
        rows: [
            { limit: 300_000, deductible: 15_000, premium: [1_600, 1_850, 2_250, 2_600, 3_100], with: { bi_extortion: [2_100, 2_500, 3_100, 3_450, 4_200] } }, // prettier-ignore
            { limit: 750_000, deductible: 15_000, premium: [2_250, 2_500, 3_200, 4_100, 5_550], with: { bi_extortion: [3_000, 3_350, 4_350, 5_450, 7_400] } }, // prettier-ignore
            { limit: 1_500_000, deductible: 15_000, premium: [2_850, 3_200, 4_100, 4_700, 6_800], with: { bi_extortion: [3_850, 4_350, 5_450, 6_300, 9_150] } }, // prettier-ignore
            { limit: 3_000_000, deductible: 15_000, premium: [3_450, 4_000, 4_950, 5_950, 7_750], with: { bi_extortion: [4_650, 5_300, 6_650, 7_900, 10_350] } }, // prettier-ignore
        ],
    },
    // Auto-bind only: a client outside these goes to QBE's underwriters, or on another insurer's form.
    eligibility: {
        revenue: { max: 30_000_000 },
        fields: ['revenue_in_malaysia', 'not_healthcare', 'not_social_ecommerce', 'not_hosting', 'not_energy', 'not_public', 'not_financial', 'not_digital_assets'], // prettier-ignore
    },
    pdf: PDF,
    sections: [
        {
            key: 'placement',
            title: 'Agency and period of insurance',
            fields: [
                field('agency_no', 'Header', 'QBE agency number', 'text', BROKER),
                field('agency_name', 'Header', 'QBE agency name', 'text', BROKER),
                field('period_from', 'Period', 'Period of insurance from', 'date', BROKER),
                field('period_to', 'Period', 'Period of insurance to', 'date', BROKER),
            ],
        },
        {
            key: 'proposer',
            title: 'A. Particulars of proposer',
            fields: [
                field('proposer_name', 'A', 'Name of proposer', 'text', ctx('company_name')),
                field('correspondence_address', 'A', 'Correspondence address', 'text', val('UW-ADDRESS', undefined, 'address')),
                field('postcode', 'A', 'Postcode', 'text', unmapped('Written with the address')),
                field('city', 'A', 'City', 'text', val('UW-ADDRESS', undefined, 'city')),
                field('state', 'A', 'State', 'text', val('UW-ADDRESS', undefined, 'state')),
                field('country', 'A', 'Country', 'text', val('UW-ADDRESS', undefined, 'country')),
                field('contact_number', 'A', 'Contact number', 'text', ctx('contact_phone')),
                field('email', 'A', 'Email', 'text', ctx('contact_email')),
                field('occupation', 'A', 'Occupation', 'text', ctx('industry')),
                field('consumer_details', 'A.1', 'Personal details (consumer contract)', 'text', na('A business takes this cover for its trade, so it is a non-consumer contract')), // prettier-ignore
                field('tax_no', 'A.2', 'Tax identification number', 'text', val('UW-TAX-NO')),
                field('registration_no', 'A.2', 'Business registration number', 'text', val('UW-REG-NO')),
                field('sst_no', 'A.2', 'SST registration number', 'text', val('UW-SST-NO'), { required: false }),
                field('office_tel', 'A.2', 'Office telephone', 'text', ctx('contact_phone')),
                field('nature_of_business', 'A.2', 'Trade, profession or nature of business', 'text', val('UW-ACTIVITIES')),
                field('established', 'A.2', 'Year the business was established', 'number', val('UW-YEAR-EST')),
            ],
        },
        {
            key: 'applicant',
            title: 'B. Applicant’s details',
            fields: [
                field('legal_name', 'B', 'Legal entity name of applicant', 'text', ctx('company_name')),
                field('applicant_registration_no', 'B', 'Registration number', 'text', val('UW-REG-NO')),
                field('industry', 'B', 'Business occupation or industry', 'text', ctx('industry')),
                field('website', 'B', 'Website', 'text', ctx('website'), { required: false }),
                field('principal_address', 'B', 'Principal address', 'text', val('UW-ADDRESS', undefined, 'address')),
                field('principal_postcode', 'B', 'Postal code', 'text', unmapped('Written with the address')),
                field('applicant_email', 'B', 'Email', 'text', ctx('contact_email')),
                field('applicant_contact', 'B', 'Contact number', 'text', ctx('contact_phone')),
                yn('revenue_within_limit', 'B(a)', 'Annual revenue does not exceed RM30m', [
                    test('UW-REVENUE-CURRENT', 'lte', 30_000_000),
                    test('Q301', 'lte', 30_000_000),
                ]),
                yn('revenue_in_malaysia', 'B(b)', 'No revenue is earned outside Malaysia', test('UW-REVENUE-OVERSEAS-PCT', 'eq', 0)),
                yn('malware_protection', 'B(c)', 'Up-to-date anti-malware, firewall and automatic virus scanning on every computer', all(any(ctl('EP-01'), has('UW-MONITORING', 'antimalware')), ctl('EP-06'))), // prettier-ignore
                yn('not_healthcare', 'B(d)(i)', 'Not in health care, hospitals or medical', not(industry('healthcare'))),
                yn('not_social_ecommerce', 'B(d)(ii)', 'Not social media, 100% e-commerce, adult entertainment or gambling', [
                    not(any(has('UW-SPECIAL-ACTIVITIES', 'social_media'), has('UW-SPECIAL-ACTIVITIES', 'adult_gambling'), has('UW-SPECIAL-ACTIVITIES', 'online_only'), test('UW-ONLINE-PCT', 'gte', 100))),
                    // Before the share of online sales is given: the activities answer says whether it sells only online.
                    not(any(has('UW-SPECIAL-ACTIVITIES', 'social_media'), has('UW-SPECIAL-ACTIVITIES', 'adult_gambling'), has('UW-SPECIAL-ACTIVITIES', 'online_only'))),
                ]), // prettier-ignore
                yn('not_hosting', 'B(d)(iii)', 'Not an ISP, telco, host, data centre, data aggregator or processor, or cyber security firm', not(any(industry('telecoms'), has('UW-SPECIAL-ACTIVITIES', 'hosting')))), // prettier-ignore
                yn('not_energy', 'B(d)(iv)', 'Not utilities, energy, oil and gas, or mining', not(any(industry('utilities', 'energy'), has('UW-SPECIAL-ACTIVITIES', 'mining')))), // prettier-ignore
                yn('not_public', 'B(d)(v)', 'Not government or education', not(industry('government', 'education'))),
                yn('not_financial', 'B(d)(vi)', 'Not a financial institution or financial services firm', not(industry('financial_services'))), // prettier-ignore
                yn('not_digital_assets', 'B(d)(vii)', 'Not decentralised finance, peer-to-peer lending, cryptocurrency, NFTs or digital assets', not(has('UW-SPECIAL-ACTIVITIES', 'digital_assets'))), // prettier-ignore
                yn('pdpa_registered', 'B(e)', 'Registered with the Personal Data Protection Commissioner', is('UW-PDPA-REG', 'registered')), // prettier-ignore
                field('pdpa_date', 'B(e)', 'Date of the PDPA certificate', 'date', val('UW-PDPA-REG-DATE'), { if: { field: 'pdpa_registered', in: ['yes'] } }), // prettier-ignore
                yn('mfa_remote_admin', 'B(f)', 'Remote access and all admin access to data and systems is protected by MFA', all(ctl('IA-01'), any(is('P-REMOTE', 'no'), ctl('IA-03')))), // prettier-ignore
            ],
        },
        {
            key: 'cover',
            title: 'Main policy cover and extensions',
            fields: [
                field('limit', 'Cover', 'Limit of indemnity (RM300,000, RM750,000, RM1,500,000 or RM3,000,000)', 'text', BROKER),
                yn('bi_extortion', 'Cover', 'With the cyber business interruption and cyber extortion extension', BROKER),
            ],
        },
        {
            key: 'claims',
            title: 'C. Claims and declaration',
            fields: [
                yn('no_outage', 'C(a)', 'Never had a system outage affecting the business', not(has('UW-LOSSES', 'system_failure'))), // prettier-ignore
                yn('no_incident', 'C(b)', 'Never had a cyber, data breach or ransomware incident, or anything that could lead to a claim', not(any(has('UW-LOSSES', 'data_breach'), has('UW-LOSSES', 'cyber_incident'), has('UW-LOSSES', 'media_claim'), has('UW-LOSSES', 'regulatory_action'), is('UW-CIRCUMSTANCES', 'yes')))), // prettier-ignore
                yn('authorised', 'C decl (a)', 'Authorised by the other entities to complete this form', DECLARATION),
                yn('read_notice', 'C decl (b)', 'Has read the notice to the proposed insured', DECLARATION),
                yn('true_complete', 'C decl (c)', 'The form and documents are true and complete', DECLARATION),
                yn('continuing_duty', 'C decl (d)', 'Will tell QBE of any change before the contract is made', DECLARATION),
                field('md_name', 'C', 'Name of managing director or CEO', 'text', DECLARATION),
                field('md_signature', 'C', 'Signature', 'text', DECLARATION),
                field('md_date', 'C', 'Date', 'date', DECLARATION),
            ],
        },
        {
            key: 'signature',
            title: 'F. Declaration and signature',
            fields: [
                yn('privacy_consent', 'F(i)', 'Agrees to the privacy statement', DECLARATION),
                yn('duty_of_care', 'F(ii)', 'Declares the answers are full and accurate, and accepts the policy terms', DECLARATION),
                yn('marketing_consent', 'F(iii)', 'Agrees to QBE using the data for marketing', DECLARATION, {
                    required: false,
                }),
                field('applicant_signature', 'F', 'Signature of applicant', 'text', DECLARATION),
                field('applicant_sign_date', 'F', 'Date', 'date', DECLARATION),
                field('applicant_name', 'F', 'Name of applicant', 'text', ctx('company_name')),
                field('intermediary_name', 'F', 'Name of intermediary', 'text', BROKER),
                field('intermediary_address', 'F', 'Intermediary’s business address and telephone', 'text', BROKER),
            ],
        },
        {
            key: 'agent',
            title: 'G. Declaration by agent, broker or officer',
            fields: [
                field('agent_name', 'G', 'Name', 'text', BROKER),
                field('agent_nric', 'G', 'NRIC number', 'text', BROKER),
                field('agent_signature', 'G', 'Signature and company stamp', 'text', BROKER),
                field('agent_date', 'G', 'Date', 'date', BROKER),
            ],
        },
    ],
};
export const QBE_AUTO_BIND = withBlocks(FORM, Object.fromEntries([...REFERRED].map((k) => [k, REFER])));
//# sourceMappingURL=qbe-auto-bind.js.map